import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useCallback, useContext } from 'react';
import { NoteInput } from 'sr-types/lib/note/v1/graphql';
import { Identity, Resource } from 'sr-types/lib/production/v1/graphql';
import { ReferenceInput, Shortlist as SearchShortlist } from 'sr-types/lib/search/v1/graphql';
import {
    BulkSaveLineItemRequest,
    LineItemInput,
    Shortlist,
    ShortlistInput,
    Summary
} from 'sr-types/lib/shortlist/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import { CONSTANTS_ID, FETCH_POLICY_CONFIG } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { searchClient, slimResultsQuery, slimSearchQuery } from '../common/list/slimQuery';
import { failedToastMessage } from '../common/utils/commonUtils';
import { RESOURCE_TYPE } from '../resource/common/resourceConstants';
import {
    bulkSaveLineItems,
    client,
    DEFAULT_ALLOWED_ACTIONS,
    deleteLineItemFromShortlist,
    deleteQuery,
    detailsQuery,
    draftShortlistAndLineItems,
    extractLineItemsFromEmailQuery,
    generateSummaryQuery,
    getLineItemReference,
    saveLineItem,
    saveLineItemWithNote,
    saveQuery,
    shortlistForCrewQuery
} from './shortlist';

export type DeleteShortlistType = {
    shortlistId: string;
    onClose?: () => void;
    onSuccess?: () => void;
    onError?: () => void;
};

export type AddLineItemToShortlistType = {
    shortlistId: string;
    itemInput: LineItemInput;
    noteInput?: NoteInput;
    onClose?: () => void;
    onSuccess?: (id: string) => void;
    onError?: () => void;
};

export type DeleteLineItemFromShortlistType = {
    shortlistId: string;
    lineItemId: string;
    onClose?: () => void;
    onSuccess?: (id: string) => void;
    onError?: () => void;
};

export const useGetOrganizationDetailsForInquiry = (filter, skip = false) => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const { loading: organizationLoading, data: organizationData } = useQuery(slimSearchQuery('Organization'), {
        client: searchClient,
        skip: filter.length === 0 || skip,
        variables: {
            entity: 'Organization',
            filter: {
                expressions: filter
            },
            page: { from: 0, size: 50 },
            tokenize: true,
            sortBy: undefined
        },
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: false
    });
    return { organizationData, organizationLoading };
};

export const useGetPersonDetailsForInquiry = (filter, skip = false) => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const { loading: personLoading, data: personData } = useQuery(slimSearchQuery('Person'), {
        client: searchClient,
        skip: filter.length === 0 || skip,
        variables: {
            entity: 'Person',
            filter: {
                expressions: filter
            },
            page: { from: 0, size: 50 },
            tokenize: true,
            sortBy: undefined
        },
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: false
    });
    return { personData, personLoading };
};

/**
 * The shortlists of the given resources: everything linked to one of them through `context`,
 * which is what the `contextType`/`contextId` filters read back.
 *
 * Several `contextId` filters are OR'ed together, so a caller holding a set of resources reads
 * all of their shortlists in one query rather than one query each. Each hit says which resource
 * it belongs to through its own `context`, which is how they are told apart again.
 */
const resourceShortlistsVariables = (resourceIds: string[]) => ({
    filters: [
        {
            identifier: 'entity',
            value: ENTITY_CONSTANTS.shortlist
        },
        { identifier: 'contextType', value: ENTITY_CONSTANTS.resource },
        ...resourceIds.map((resourceId) => ({ identifier: 'contextId', value: resourceId }))
    ],
    page: { size: 1000, from: 0 },
    sortBy: 'createdOn_ASC'
});

export const useGetResourceShortlists = (resourceId: string, skip: boolean, includeAllActions: boolean = false) => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const { data: resourceShortlistsData, loading: resourceShortlistsLoading } = useQuery(
        slimResultsQuery(ENTITY_CONSTANTS.shortlist, includeAllActions),
        {
            variables: resourceShortlistsVariables([resourceId]),
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            },
            client: searchClient,
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
            notifyOnNetworkStatusChange: true,
            skip: skip
        }
    );

    return { resourceShortlistsData, resourceShortlistsLoading };
};

/**
 * Imperative counterpart of `useGetResourceShortlists`, for save flows that need a set of
 * resources' shortlists at one point in time rather than as rendered state - reading back the
 * shortlist the service creates along with each resource, for instance.
 *
 * Every resource asked for is read in the one query, so a flow that just saved several of them
 * pays for one round trip rather than one per resource. Hits come back carrying their own
 * `context`, which is what the caller matches them back to a resource by.
 */
export const useGetResourceShortlistsLazily = () => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const [fetchResourceShortlists, { loading: resourceShortlistsLoading }] = useLazyQuery(
        slimResultsQuery(ENTITY_CONSTANTS.shortlist),
        {
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            },
            client: searchClient,
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
        }
    );

    const getResourceShortlists = useCallback(
        async (resourceIds: string[]): Promise<SearchShortlist[]> => {
            if (!resourceIds.length) {
                return [];
            }

            const { data } = await fetchResourceShortlists({ variables: resourceShortlistsVariables(resourceIds) });
            return data?.results?.hits?.items ?? [];
        },
        [fetchResourceShortlists]
    );

    return { getResourceShortlists, resourceShortlistsLoading };
};

export const useDeleteShortlist = (activeOrganizationAccount) => {
    const [shortlistDelete, { loading: isShortlistDeleting }] = useMutation(deleteQuery, { client });

    const deleteShortlist = ({
        shortlistId,
        onClose = undefined,
        onSuccess = undefined,
        onError = undefined
    }: DeleteShortlistType): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            shortlistDelete({
                variables: { id: shortlistId },
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    }
                }
            })
                .then((res) => {
                    if (res?.data?.deleteShortlist?.errors) {
                        onError?.();
                        reject(res.data.deleteShortlist.errors);
                    } else {
                        onSuccess?.();
                        resolve();
                    }
                })
                .catch((err) => {
                    onError?.();
                    reject(err);
                })
                .finally(() => {
                    onClose?.();
                    setTimeout(() => {
                        searchClient.refetchQueries({
                            include: ['slimResultsQuery']
                        });
                    }, 300);
                });
        });
    };

    return { deleteShortlist, isShortlistDeleting };
};

export const useAddShortlistLineItem = (activeOrganizationAccount) => {
    const [addItem, { loading: isLineItemSaving }] = useMutation(saveLineItem, { client });

    const addShortlistLineItem = useCallback(
        async ({
            shortlistId,
            itemInput,
            onClose = undefined,
            onSuccess = undefined,
            onError = undefined
        }: AddLineItemToShortlistType): Promise<void> => {
            try {
                const res = await addItem({
                    variables: {
                        shortlistId,
                        input: itemInput
                    },
                    context: {
                        headers: {
                            ownerId: activeOrganizationAccount
                        }
                    }
                });

                if (res?.data?.saveLineItem?.errors) {
                    throw res.data.saveLineItem.errors;
                }

                onSuccess?.(res?.data?.saveLineItem?.id);
            } catch (error) {
                onError?.();
                throw error;
            } finally {
                onClose?.();
            }
        },
        [addItem, activeOrganizationAccount]
    );

    return { addShortlistLineItem, isLineItemSaving };
};

export const useAddShortlistLineItemWithNote = (activeOrganizationAccount) => {
    const [addItemWithNote, { loading: isLineItemWithNotesSaving }] = useMutation(saveLineItemWithNote, { client });

    const addShortlistLineItemWithNote = ({
        shortlistId,
        itemInput,
        noteInput,
        onClose = undefined,
        onSuccess = undefined,
        onError = undefined
    }: AddLineItemToShortlistType): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            addItemWithNote({
                variables: {
                    shortlistId,
                    input: itemInput,
                    note: noteInput
                },
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    }
                }
            })
                .then((res) => {
                    if (res?.data?.saveLineItemWithNote?.errors) {
                        onError?.();
                        reject(res.data.saveLineItemWithNote.errors);
                    } else {
                        onSuccess?.(res?.data?.saveLineItemWithNote?.id);
                        resolve();
                    }
                })
                .catch((error) => {
                    onError?.();
                    reject(error);
                })
                .finally(() => {
                    onClose?.();
                });
        });
    };

    return { addShortlistLineItemWithNote, isLineItemWithNotesSaving };
};

export const useDeleteLineItemFromShortlist = () => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [deleteLineItem, { loading: isLineItemDeleting }] = useMutation(deleteLineItemFromShortlist, { client });

    const deleteShortlistLineItem = ({ shortlistId, lineItemId }: DeleteLineItemFromShortlistType) => {
        return deleteLineItem({
            variables: {
                shortlistId,
                lineItemId
            },
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            }
        });
    };

    return { deleteShortlistLineItem, isLineItemDeleting };
};

export const useDraftShortlistAndLineItems = () => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const [fetchDraftShortlistAndLineItems, { data, loading, error }] = useLazyQuery(draftShortlistAndLineItems, {
        client,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
    });

    return {
        fetchDraftShortlistAndLineItems,
        draftShortlistData: data,
        draftShortlistLoading: loading,
        draftShortlistError: error
    };
};

export const useBulkSaveLineItem = () => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const [save, { loading: isSaving, error: saveError }] = useMutation(bulkSaveLineItems, {
        client: client,
        context: { headers: { ownerId: activeOrganizationAccount } }
    });

    const saveLineItems = async (input: BulkSaveLineItemRequest) => {
        try {
            const res = await save({
                variables: {
                    input
                }
            });

            const data = res?.data;

            const results = data?.bulkSaveLineItems?.results ?? [];
            let hasErrors = false;

            results.forEach((r, index) => {
                if (r.errors?.length) {
                    if (!r.reference) {
                        hasErrors = true;
                    }
                }
            });

            return {
                hasErrors,
                results,
                createdCount: data.bulkSaveLineItems.createdCount,
                updatedCount: data.bulkSaveLineItems.updatedCount,
                unprocessedCount: data.bulkSaveLineItems.unprocessedCount
            };
        } catch (err: unknown) {
            failedToastMessage('shortlist.lineItem.bulkSave.error');
            throw err;
        }
    };

    return { saveLineItems, isSaving, saveError };
};

export const useReadWriteGql = (entityId: string) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const create = entityId === CONSTANTS_ID.createId;

    const {
        loading: isLoading,
        data,
        error: loadError
    } = useQuery(detailsQuery, {
        variables: { id: entityId },
        client,
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        skip: create
    });

    const [saveMutation, { loading: isSaving, error: saveError }] = useMutation(saveQuery, {
        client: client,
        context: { headers: { ownerId: activeOrganizationAccount } }
    });

    return { isLoading, isSaving, loadError, saveError, data, saveMutation };
};

export const toInput = (id: string, entity: Shortlist, resData?: Resource): ShortlistInput => {
    const roles = entity?.roles?.length
        ? entity.roles
        : (resData?.type === RESOURCE_TYPE.crew || resData?.type === RESOURCE_TYPE.cast) && resData?.role
          ? [resData.role]
          : [];
    const categories = entity?.categories?.length
        ? entity?.categories?.map((category) => ({ value: category, lineage: { value: category } }))
        : resData?.type !== RESOURCE_TYPE.cast && resData?.type !== RESOURCE_TYPE.crew && resData?.resourceCategory
          ? [resData.resourceCategory]
          : [];
    const sequence = entity?.lineItems?.length
        ? (() => {
              const lineItemIds = entity.lineItems.map((item) => item.identity?.id).filter(Boolean);
              const lineItemIdSet = new Set(lineItemIds);
              const filteredSequence = (entity.sequence || []).filter((seq) => lineItemIdSet.has(seq.id));

              const existingSequenceIds = new Set(filteredSequence.map((seq) => seq.id));
              const newSequences = lineItemIds
                  .filter((id) => !existingSequenceIds.has(id))
                  .map((id) => ({ ...getLineItemReference(id) }) as ReferenceInput);
              return [...filteredSequence, ...newSequences];
          })()
        : null;
    return {
        ...entity,
        identity: {
            id: id === CONSTANTS_ID.createId ? '' : id
        },
        type: entity?.type || resData?.type || '',
        name: entity?.name || resData?.name || '',
        roles,
        sequence: sequence || entity?.sequence || null,
        categories
    };
};

export const fromInput = (entityInput: ShortlistInput, addSecurity: boolean = false): Shortlist => {
    const shortlistObj: Shortlist = {
        identity: entityInput.identity as Identity,
        name: entityInput.name,
        categories: entityInput.categories,
        roles: entityInput.roles,
        type: entityInput.type,
        summary: entityInput.summary as Summary
    };
    if (addSecurity) {
        shortlistObj.security = { allowedActions: DEFAULT_ALLOWED_ACTIONS };
    }
    return shortlistObj;
};

export type ShortlistTypeSuperset = Shortlist & ShortlistInput;

export const useGenerateSummary = (shortlistLineItemId: string, url: string, skip: boolean) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const { data: summary, loading: summaryLoading } = useQuery(generateSummaryQuery, {
        client,
        variables: { shortlistLineItemId, url, size: 'medium' },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        skip
    });

    return { summary, summaryLoading };
};

export const useFetchCrew = () => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [fetchCrew, { loading: aiSearchLoading, error: aiError }] = useLazyQuery(shortlistForCrewQuery, {
        client,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
    });

    return { fetchCrew, aiSearchLoading, aiError };
};

export const useExtractLineItemsFromEmail = () => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [extractLineItemsFromEmail, { loading: emlExtractLoading, error: emlExtractError }] = useLazyQuery(
        extractLineItemsFromEmailQuery,
        {
            client,
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            },
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
        }
    );

    return { extractLineItemsFromEmail, emlExtractLoading, emlExtractError };
};
